import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { faCircleCheck, faPercent, faToggleOn } from "@fortawesome/free-solid-svg-icons"
import List from "../../../components/List/List"
import { AdminListCard, AdminListMetricItem } from "../components/AdminListCard"
import { AdminMetricCard } from "../components/AdminMetricCard"
import { ResellerCommissionPlan } from "../../../Objects/AdminReseller/ResellerCommissionPlan"
import { EResellerCommissionDurationType } from "../../../Enum/EResellerCommissionDurationType"
import { useAdminReseller } from "../../../hooks/AdminDashboard/useAdminReseller"
import { ResellerCommissionPlanPopup } from "../ResellerCommissionPlanPopup"

export const ResellerCommissionPlansSection: React.FC = () => {
  const { t } = useTranslation("admin")

  const { handleGetAllCommissionPlans, handleCreateCommissionPlan, handleUpdateCommissionPlan } = useAdminReseller()

  const [plans, setPlans] = useState<ResellerCommissionPlan[]>([])

  const [isPlanPopupOpen, setIsPlanPopupOpen] = useState(false)

  const [editingPlan, setEditingPlan] = useState<ResellerCommissionPlan | null>(null)

  const refreshPlans = () => handleGetAllCommissionPlans(setPlans)

  useEffect(() => {
    refreshPlans()
  }, [])

  const handleAddPlan = () => {
    setEditingPlan(null)
    setIsPlanPopupOpen(true)
  }

  const handleEditPlan = (id: number) => {
    const plan = plans.find((item) => item.id === id)

    if (!plan) return

    setEditingPlan(plan)
    setIsPlanPopupOpen(true)
  }

  const formatDuration = (plan: ResellerCommissionPlan) =>
    plan.durationType === EResellerCommissionDurationType.FixedMonths
      ? t("reseller_commission_plan_duration_fixed_months_value", { months: plan.durationInMonths })
      : t("reseller_commission_plan_duration_recurring")

  return (
    <div className="w-full overflow-auto bg-white p-1 md:p-0">
      <div className="grid grid-cols-1 gap-4 p-1 md:grid-cols-3">
        <AdminMetricCard
          title={t("reseller_commission_plans_count")}
          value={`${plans.length}`}
          icon={faPercent}
          tone="brand"
        />

        <AdminMetricCard
          title={t("reseller_status_active")}
          value={`${plans.filter((item) => item.isActive).length}`}
          icon={faToggleOn}
          tone="positive"
        />

        <AdminMetricCard
          title={t("reseller_commission_plan_is_default")}
          value={plans.find((item) => item.isDefault)?.name ?? t("reseller_no")}
          icon={faCircleCheck}
          tone="violet"
        />
      </div>

      <div className="p-2">
        <List
          messageCount={t("reseller_commission_plans_count")}
          messageEmpty={t("reseller_commission_plans_empty")}
          nameFindSearch={t("reseller_commission_plans_search")}
          handleAddButton={handleAddPlan}
          handleRefreshButton={refreshPlans}
          handleOnClick={handleEditPlan}
          data={plans.map((item, index) => {
            const status = item.isActive ? t("reseller_status_active") : t("reseller_status_inactive")

            return {
              id: item.id,
              index,
              title: item.name,
              linkImage: "",
              isDisable: !item.isActive,
              dataFilter: [item.name, status],
              children: (
                <AdminListCard
                  title={item.name}
                  subtitle={`${item.commissionPercentage}% • ${formatDuration(item)}`}
                  badge={status}
                  badgeClass={item.isActive ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}
                  cardClass={
                    item.isActive
                      ? "rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3"
                      : "rounded-xl border-2 border-slate-200 bg-slate-50 p-3"
                  }
                >
                  <div className="gap-2">
                    <AdminListMetricItem
                      label={t("reseller_commission_plan_percentage_placeholder")}
                      value={`${item.commissionPercentage}%`}
                      accent="text-orange-700"
                    />

                    <AdminListMetricItem
                      label={t("reseller_commission_plan_duration_type")}
                      value={formatDuration(item)}
                    />

                    <AdminListMetricItem
                      label={t("reseller_commission_plan_is_default")}
                      value={item.isDefault ? t("reseller_yes") : t("reseller_no")}
                    />
                  </div>
                </AdminListCard>
              ),
            }
          })}
        />
      </div>

      {isPlanPopupOpen && (
        <ResellerCommissionPlanPopup
          plan={editingPlan}
          onClose={() => setIsPlanPopupOpen(false)}
          onSave={(planId, request) => {
            const handleSuccess = () => {
              setIsPlanPopupOpen(false)
              refreshPlans()
            }

            if (planId) {
              handleUpdateCommissionPlan(planId, request, handleSuccess)

              return
            }

            handleCreateCommissionPlan(request, handleSuccess)
          }}
        />
      )}
    </div>
  )
}
